"use client";

import { skipToken, useQuery } from "@apollo/client/react";
import Image from "next/image";
import { useContext, useState } from "react";
import { LuBadgeDollarSign, LuCrown, LuGem } from "react-icons/lu";

import { setDialog } from "@/components/app/dialogs/dialog-provider";
import { FollowUserListsButton } from "@/components/app/lists/follow-user-lists-button";
import { MessageLink } from "@/components/app/messages/message-link";
import { ProfileActionsDropdown } from "@/components/app/profile/profile-actions-dropdown";
import { ProfileStat } from "@/components/app/profile/profile-stat";
import { SubscribeButton } from "@/components/app/subscribe-button";
import CreatorCoinExchangeWrapper from "@/components/shared/creator-coin-exchange-modal/creator-coin-exchange-wrapper";
import { DesktopTooltip } from "@/components/shared/desktop-tooltip";
import DesoAvatar from "@/components/shared/deso-avatar";
import { DiamondSlider } from "@/components/shared/diamond-slider";
import { HolderBadge } from "@/components/shared/holder-badge";
import { TriggerLoggedInModalWindow } from "@/components/shared/is-logged-in";
import { UserFollowCounts } from "@/components/shared/user-follow-counts";
import { UserVerifiedIcon } from "@/components/shared/user-verified-icon";
import { UserWhaleIcon } from "@/components/shared/user-whale-icon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/components/ui/use-toast";
import { UserContext } from "@/contexts/user-context";
import type { CreatorStat } from "@/graphql/codegen/graphql";
import {
  CreatorStatByIdDocument,
  TokenBalancesDocument,
} from "@/graphql/codegen/graphql";
import { useCoinPropertiesQuery } from "@/hooks/use-coin-properties-query";
import { useFetchCurrentAccount } from "@/hooks-api/use-fetch-current-account";
import { useUserFollowStatus } from "@/hooks-api/use-user-follow-status";
import { DIALOG } from "@/utils/dialog-routes";
import { getUserProfilePicture, pluralize } from "@/utils/helpers";
import { cn } from "@/utils/lib";
import {
  getMarkdownBody,
  smartPostBodyCut as smartTextContentCut,
} from "@/utils/post-utils";
import { getTotalSubscribersCount } from "@/utils/subscriptions";
import { getUserDisplayName, getUserName } from "@/utils/usernames";

import DiscoverStatFooter from "../discover/discover-stat-footer";
import UserConnectedSocialsList from "../user-connected-socials-list";
import { UsernamePageHeaderCoinPrice } from "./username-page-header-coin-price";

const MIN_CHAR_TO_SHOW = 200;

export const UserNamePageHeaderSkeleton = () => (
  <div>
    <Skeleton className="aspect-1400/400 w-full rounded-none" />

    <div
      className="
        flex items-start gap-4 p-6
      "
    >
      <div
        className="
          relative -mt-16 flex size-[clamp(4.5rem,21vw,7.5rem)] shrink-0
          cursor-pointer overflow-hidden rounded-full border-4 border-background
          hover:border-secondary
          sm:-mt-20
        "
      >
        <div className="size-full bg-accent" />
      </div>

      <div
        className="
          ml-auto flex max-w-[calc(100%-5.5rem)] shrink-0 flex-row-reverse
          flex-wrap items-start justify-end gap-2
          sm:max-w-none sm:flex-row
        "
      >
        <Skeleton className="size-[40px] rounded-full" />

        <div
          className="
            flex flex-row-reverse gap-2
            sm:flex-row
          "
        >
          <Skeleton className="size-[40px] rounded-full" />
          <Skeleton className="size-[40px] rounded-full" />
          <Skeleton className="h-[40px] w-[70px] rounded-full" />
        </div>
      </div>
    </div>

    <div className="mx-6 my-2 flex w-full max-w-[70%] flex-col gap-2">
      <Skeleton className="h-7 w-full" />
      <Skeleton className="my-2 h-6 w-full" />
      <div className="flex max-w-[280px] basis-full gap-4">
        <Skeleton className="h-6 w-full" />
        <Skeleton className="h-6 w-full" />
      </div>
    </div>

    <div className="mt-5" />
  </div>
);

export const UsernamePageHeader = ({
  publicKey,
  username,
}: {
  publicKey: string;
  username?: string;
}) => {
  const { refetchUser, currentUserPublicKey } = useContext(UserContext);

  const [showFullDescription, setShowFullDescription] = useState(false);
  const { toast } = useToast();
  const [sendDiamondsLoading, setSendDiamondsLoading] = useState(false);

  const {
    fetchAccountData,
    accountLoading,
    fullAccountData,
    fullAccount,
    isMyAccount,
  } = useFetchCurrentAccount({ publicKey });

  const hasShortBody =
    (fullAccount?.profile?.description?.length ?? 0) > MIN_CHAR_TO_SHOW;
  const description =
    hasShortBody && !showFullDescription
      ? smartTextContentCut(
          fullAccount?.profile?.description ?? "",
          MIN_CHAR_TO_SHOW,
        )
      : fullAccount?.profile?.description;

  const { isPublicKeyFollowedByUser } = useUserFollowStatus(publicKey || "");
  const accountPublicKey = fullAccountData?.accountByPublicKey?.publicKey;

  const { data: creatorStatData, loading: creatorStatLoading } = useQuery(
    CreatorStatByIdDocument,
    accountPublicKey
      ? {
          variables: {
            publicKeyBase58Check: accountPublicKey,
          },
        }
      : skipToken,
  );

  const { data: coinPropertiesData, isLoading: loadingCoinProperties } =
    useCoinPropertiesQuery({
      publicKey: accountPublicKey || "",
    });

  const { data: tokenBalancesData } = useQuery(
    TokenBalancesDocument,
    currentUserPublicKey && accountPublicKey
      ? {
          variables: {
            filter: {
              creator: {
                publicKey: {
                  equalTo: currentUserPublicKey,
                },
              },
              isDaoCoin: {
                equalTo: true,
              },
              holder: {
                publicKey: {
                  equalTo: accountPublicKey,
                },
              },
            },
          },
        }
      : skipToken,
  );
  const creatorStat =
    (creatorStatData?.creatorStat as CreatorStat | null | undefined) ??
    (fullAccount && !creatorStatLoading
      ? ({
          totalAssetsBalanceUsdCents: fullAccount.totalBalanceUsdCents ?? "0",
          publicKeyBase58Check: fullAccount.publicKey,
        } as CreatorStat)
      : null);

  if (accountLoading || loadingCoinProperties) {
    return <UserNamePageHeaderSkeleton />;
  }

  if (!publicKey) {
    throw new Error("Public key not found");
  }

  const extraData = fullAccount?.extraData;
  const followersCount = fullAccount?.followerCounts?.totalFollowers ?? 0;
  const followingCount = fullAccount?.followingCounts?.totalFollowing ?? 0;
  const hasSubscriptionTiers =
    (fullAccount?.subscriptionTiers?.totalCount ?? 0) > 0;
  const subscribersCount = fullAccount
    ? getTotalSubscribersCount(fullAccount)
    : 0;
  const accountsCurrentUserTokenBalance =
    tokenBalancesData?.tokenBalances?.nodes?.[0]?.balanceNanos ?? 0;
  const featuredImage = fullAccount?.extraData?.FeaturedImageURL;
  const usernameToRender = fullAccountData?.accountByPublicKey?.username;
  const userLabel = getUserDisplayName({
    extraData,
    publicKey,
    username: usernameToRender ?? username ?? "",
    truncateLength: 40,
  });

  const renderProfileActions = () => {
    return (
      <div
        className="
          flex max-w-[calc(100%-5.5rem)] shrink-0 flex-row-reverse flex-wrap
          justify-end gap-2 self-start
          sm:max-w-none sm:items-start
          md:flex-row
        "
      >
        <div className="relative">
          {fullAccount && (
            <ProfileActionsDropdown
              account={fullAccount}
              onRefetch={fetchAccountData}
            />
          )}
        </div>
        {!isMyAccount && (
          <>
            <DiamondSlider
              {...{
                params: {
                  publicKey,
                },
                creatorPublicKey: currentUserPublicKey,
                onDiamondSend: () => {
                  toast({
                    title: "Sending diamonds...",
                    variant: "notification",
                    description: `Sending diamonds to ${usernameToRender} started.`,
                  });
                  setSendDiamondsLoading(true);
                },
                onDiamondSent: () => {
                  toast({
                    title: "Diamonds sent",
                    variant: "success",
                    description: `You sent diamonds to ${usernameToRender}.`,
                  });
                  setSendDiamondsLoading(false);

                  refetchUser();
                },
                onDiamondError: () => {
                  toast({
                    title: "Error",
                    variant: "destructive",
                    description: `An error happened when sending diamonds to ${usernameToRender}.`,
                  });
                  setSendDiamondsLoading(false);
                },
                hoverTrigger: (
                  <Button
                    variant="outline"
                    size="icon"
                    loading={sendDiamondsLoading}
                  >
                    <LuGem className="size-4" />
                  </Button>
                ),
              }}
            />
            <div className="hidden sm:contents">
              <MessageLink publicKey={publicKey} />
            </div>
            <FollowUserListsButton
              userLabel={userLabel}
              userPublicKey={publicKey}
              variant="profile"
            />
            {hasSubscriptionTiers && !!fullAccount && (
              <SubscribeButton
                creator={fullAccount}
                renderContext="profile"
                renderTrigger={({ isSubscribed, loading }) => {
                  if (isSubscribed) {
                    return (
                      <DesktopTooltip content="Unsubscribe">
                        <Button
                          variant="default"
                          className="size-10 shrink-0 px-0"
                          loading={loading}
                        >
                          <LuCrown className="size-4 shrink-0" />
                          <span className="sr-only">Unsubscribe</span>
                        </Button>
                      </DesktopTooltip>
                    );
                  }

                  return (
                    <div className="contents">
                      <Button
                        variant="outlinePrimary"
                        className="
                          hidden h-10 shrink-0 gap-2 px-3
                          sm:inline-flex
                        "
                        loading={loading}
                      >
                        <LuCrown className="size-4 shrink-0" />
                        <span>Subscribe</span>
                      </Button>
                      <DesktopTooltip content="Subscribe">
                        <Button
                          variant="outlinePrimary"
                          className="inline-flex size-10 shrink-0 px-0 sm:hidden"
                          loading={loading}
                        >
                          <LuCrown className="size-4 shrink-0" />
                          <span className="sr-only">Subscribe</span>
                        </Button>
                      </DesktopTooltip>
                    </div>
                  );
                }}
              />
            )}
          </>
        )}
      </div>
    );
  };

  const coverImageUrl = featuredImage || "/assets/empty-39.png";

  const openImageViewer = (url: string, alt: string) => {
    setDialog(DIALOG.ImageViewer, {
      open: true,
      params: {
        alt,
        url,
      },
    });
  };

  return (
    <div className="p-0">
      <div className="unforce-radius relative aspect-1400/400 w-full overflow-hidden">
        <button
          type="button"
          className="block size-full cursor-pointer bg-transparent p-0"
          onClick={() => {
            openImageViewer(coverImageUrl, "Cover Image");
          }}
        >
          <Image
            src={coverImageUrl}
            fill
            sizes="(min-width: 768px) 700px, 100vw"
            quality={90}
            alt="Cover Image"
            className="unforce-radius cursor-pointer object-cover"
            priority
          />
        </button>
      </div>
      <div
        className="
          relative flex items-start justify-between p-4 pb-4 sm:p-6
          sm:pb-2
        "
      >
        <div className="flex min-w-0 flex-1 gap-2">
          <button
            type="button"
            className="
              -mt-16 -ml-1 block size-[clamp(4.5rem,21vw,7.5rem)] shrink-0
              bg-transparent p-0
              sm:-mt-20 sm:ml-0
            "
            onClick={() => {
              if (isMyAccount) {
                setDialog(DIALOG.UpdateProfile, {
                  open: true,
                  props: {
                    onUpdated: fetchAccountData,
                  },
                });
                return;
              }

              openImageViewer(
                getUserProfilePicture(fullAccount),
                "Avatar Image",
              );
            }}
          >
            <DesoAvatar
              clickable={false}
              size={120}
              account={
                fullAccount ?? {
                  publicKey,
                  username: "",
                  extraData: {},
                }
              }
              borderSize={4}
              className={cn(
                `
                  relative size-[clamp(4.5rem,21vw,7.5rem)]! cursor-pointer
                  border border-background bg-background
                  [&_.deso-avatar-nft-frame]:inset-1!
                  [&_.deso-avatar-nft-frame]:size-[calc(100%-0.5rem)]!
                  [&_.deso-avatar-nft-image]:size-full!
                  [&_.deso-avatar-nft-image]:object-cover
                `,
                isMyAccount && "cursor-pointer",
              )}
            />
          </button>
          <div className="flex items-center gap-2">
            <UserConnectedSocialsList
              publicKey={publicKey}
              isMyAccount={isMyAccount}
            />
          </div>
        </div>

        <TriggerLoggedInModalWindow>
          {renderProfileActions()}
        </TriggerLoggedInModalWindow>
      </div>
      <div className="-mt-4 px-4 py-2 sm:px-6 sm:pb-4">
        <div
          className="
            flex flex-col-reverse gap-3
            sm:flex-col
          "
        >
          <div className="flex gap-2">
            {isPublicKeyFollowedByUser && !isMyAccount && (
              <Badge
                className="
                  relative mb-4 inline-block w-fit text-xs
                  sm:mb-0
                "
                variant="outline"
              >
                Follows you
              </Badge>
            )}
            {!!accountsCurrentUserTokenBalance && !isMyAccount && (
              <HolderBadge />
            )}
          </div>
          <div className="flex flex-col gap-2">
            <div
              className="
                mb-2 flex flex-col
                sm:mb-0 sm:flex-row sm:items-center sm:gap-2
              "
            >
              <div className="flex items-center gap-2">
                <h2
                  className="
                    flex gap-2 font-sans text-lg font-semibold
                    text-muted-foreground
                  "
                >
                  {getUserDisplayName({
                    extraData,
                    publicKey: publicKey,
                    username: usernameToRender,
                  })}
                </h2>
                {fullAccount && (
                  <>
                    <UserVerifiedIcon
                      {...{ account: fullAccount, width: 24, height: 24 }}
                    />
                    <UserWhaleIcon
                      {...{ account: fullAccount, width: 28, height: 28 }}
                      className="relative top-[-4px]"
                    />
                  </>
                )}
              </div>
              <p className="text-sm text-muted">
                {getUserName({
                  account: fullAccount,
                })}
              </p>
            </div>
            <div
              className="
                flex flex-nowrap items-start gap-5 pb-2
                sm:flex-wrap sm:items-center sm:gap-4
                sm:pb-4
              "
            >
              <UserFollowCounts
                className="contents sm:flex sm:items-center sm:gap-4"
                statClassName="max-w-20 sm:max-w-none"
                variant="responsiveStacked"
                {...{
                  publicKey: publicKey,
                  username: username ?? "",
                  followersCount,
                  followingCount,
                }}
              />
              {hasSubscriptionTiers && (
                <button
                  type="button"
                  className="min-w-0 text-left"
                  onClick={() =>
                    setDialog(DIALOG.Subscribers, {
                      open: true,
                      params: {
                        publicKey,
                        subscribersCount: String(subscribersCount),
                        ...(username ? { username } : {}),
                      },
                    })
                  }
                >
                  <ProfileStat
                    count={subscribersCount}
                    label={pluralize(subscribersCount, "Subscriber", {
                      includeCount: false,
                    })}
                    icon={<LuBadgeDollarSign className="size-4 text-muted" />}
                    className="max-w-20 sm:max-w-none"
                    variant="responsiveStacked"
                  />
                </button>
              )}
            </div>

            <div className="flex w-full flex-row flex-wrap items-center">
              {coinPropertiesData?.ammRunning && coinPropertiesData.hasCoin && (
                <div className={cn(coinPropertiesData?.hasCoin && "mb-2")}>
                  <UsernamePageHeaderCoinPrice
                    tokenDetails={coinPropertiesData}
                  />
                </div>
              )}
              {fullAccount?.creatorBasisPoints && (
                <div className={cn(coinPropertiesData?.hasCoin && "mb-2")}>
                  <CreatorCoinExchangeWrapper publicKey={publicKey} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div>
          {description && (
            <div className="ProseMirror prose mb-4 text-sm whitespace-break-spaces">
              {getMarkdownBody(description ?? "")}
              {!showFullDescription && hasShortBody && (
                <Button
                  variant="link"
                  className="my-2 block pl-0"
                  onClick={() => {
                    setShowFullDescription(true);
                  }}
                >
                  Read More...
                </Button>
              )}
            </div>
          )}

          {creatorStatLoading ? (
            <Skeleton className="my-2 h-16 w-full" />
          ) : creatorStat !== null ? (
            <div className="my-2">
              <DiscoverStatFooter user={creatorStat} />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
